import { useCallback, useMemo } from "react";

import { useCentinelaLiveStore } from "../../../features/centinela/liveStore";
import { useCentinelaUnits } from "../../../features/centinela/queries";
import { useDriversList } from "../../../features/drivers/queries";
import { useAllUnits } from "../../../features/units/queries";
import { useVehiclesSocketStore } from "../../../store/admin/vehiclesSocketStore";
import { useCentinelaDemo } from "../services/centinelaDemoMode";
import type { Unit } from "../../../store/admin/unitsStore";
import type { CentinelaUnit } from "../../../features/centinela/types";
import {
  emptyWatchState,
  mapCentinelaBreakdown,
  mapCentinelaUnit,
  UNKNOWN_WATCH_STATUS,
} from "../services/mapCentinelaUnit";
import { sortUnitDevices } from "../../../features/devices/pairing";
import type { MockUnitRef } from "../services/centinelaMocks";
import type { FleetStatusBreakdown, UnitWatchState, WatchStatus } from "../types";

export interface WatchedUnit {
  unit: Unit;
  watch: UnitWatchState;
}

const DRIVERS_LIMIT = 500;

export function useWatchedFleet() {
  const isDemo = useCentinelaDemo();
  const { data, isLoading, isError, error, refetch: refetchUnits } = useAllUnits();
  const drivers = useDriversList({ limit: DRIVERS_LIMIT });
  const centinela = useCentinelaUnits();
  const vehicles = useVehiclesSocketStore((state) => state.vehicles);
  const vehiclesMap = useVehiclesSocketStore((state) => state.vehiclesMap);
  const callingDeviceIds = useCentinelaLiveStore((state) => state.callingDeviceIds);

  const driverByVehicleId = useMemo(() => {
    const rows = drivers.data?.data ?? [];
    return new Map(
      rows
        .map((driver) => ({
          vehicleId: driver.vehicle_id ?? driver.vehicleId ?? null,
          id: driver.id,
          name: `${driver.firstName ?? ""} ${driver.lastName ?? ""}`.trim(),
        }))
        .filter((entry) => entry.vehicleId !== null && entry.name.length > 0)
        .map((entry) => [entry.vehicleId as string, { id: entry.id, name: entry.name }]),
    );
  }, [drivers.data]);

  const centinelaByDeviceId = useMemo(() => {
    const rows = centinela.data?.units ?? [];
    return new Map<string, CentinelaUnit>(rows.map((row) => [row.device_id, row]));
  }, [centinela.data]);

  const centinelaOf = useCallback(
    (unit: Unit): CentinelaUnit | undefined =>
      (unit.devices ?? [])
        .map((device) => centinelaByDeviceId.get(device.id))
        .find((row): row is CentinelaUnit => Boolean(row)),
    [centinelaByDeviceId],
  );

  const units = useMemo<Unit[]>(() => {
    const rows = data ?? [];
    return rows.map((unit) => {
      const driver = unit.driver ?? driverByVehicleId.get(unit.id);
      if (driver) return { ...unit, driver };

      const servedDriver = centinelaOf(unit)?.driver?.trim();
      return servedDriver ? { ...unit, driver: { id: "", name: servedDriver } } : unit;
    });
  }, [data, driverByVehicleId, centinelaOf]);

  const refs = useMemo<MockUnitRef[]>(
    () => units.map((unit) => ({ id: unit.id, name: unit.name })),
    [units],
  );

  const liveOf = useMemo(() => {
    return (deviceId: string) => {
      const index = vehiclesMap.get(deviceId);
      return index === undefined ? undefined : vehicles[index];
    };
  }, [vehicles, vehiclesMap]);

  const watchStates = useMemo<UnitWatchState[]>(
    () =>
      units.map((unit) => {
        const devices = unit.devices ?? [];
        const match = centinelaOf(unit);
        const live = devices.map((device) => liveOf(device.id)).find((row) => Boolean(row));
        const hasCoords =
          live != null &&
          live.latitude != null &&
          live.longitude != null &&
          (live.latitude !== 0 || live.longitude !== 0);
        const fuelPercent = devices.find((device) => device.fuelLevel > 0)?.fuelLevel ?? 0;
        const telemetry = {
          fuelPercent,
          speedKph: live && live.speed != null ? Math.round(live.speed) : null,
          // El rumbo del último reporte, para dibujar flecha en vez de punto
          // cuando va en marcha. `course` viene del `heading` del equipo.
          headingDeg: live && Number.isFinite(live.course) ? live.course : null,
          // La foto del EXPEDIENTE del conductor, la que sube una persona y va
          // con su nombre. No confundir con las capturas de cabina: aquélla
          // identifica, ésta sólo muestra a quien iba sentado.
          driverPhoto: live?.driver?.photo || null,
          position: hasCoords ? { lat: live.latitude, lng: live.longitude } : null,
          updatedAt: live?.lastReport?._seconds
            ? new Date(live.lastReport._seconds * 1000).toISOString()
            : null,
          address: live?.address || unit.lastAddress || null,
        };
        const base = match ? mapCentinelaUnit(match, telemetry) : emptyWatchState(unit.id);
        const isCalling = devices.some((device) => callingDeviceIds.includes(device.id));
        return {
          ...base,
          unitId: unit.id,
          deviceId: match?.device_id ?? sortUnitDevices(devices)[0]?.id ?? null,
          fuelPercent,
          isCalling,
        };
      }),
    [units, centinelaOf, liveOf, callingDeviceIds],
  );

  const watchedUnits = useMemo<WatchedUnit[]>(
    () => units.map((unit, index) => ({ unit, watch: watchStates[index] })),
    [units, watchStates],
  );

  const breakdown = useMemo<FleetStatusBreakdown>(
    () =>
      watchStates.reduce<FleetStatusBreakdown>(
        (acc, state) => ({ ...acc, [state.status]: acc[state.status] + 1 }),
        mapCentinelaBreakdown(undefined),
      ),
    [watchStates],
  );

  const statuses = useMemo(
    () => new Map(watchStates.map((state) => [state.unitId, state.status])),
    [watchStates],
  );

  const countOf = (status: WatchStatus) => breakdown[status];

  const refresh = useCallback(() => {
    void refetchUnits();
    void centinela.refetch();
  }, [refetchUnits, centinela.refetch]);

  return {
    units,
    refs,
    watchedUnits,
    breakdown,
    countOf,
    refresh,
    statusOf: (unitId: string): WatchStatus => statuses.get(unitId) ?? UNKNOWN_WATCH_STATUS,
    isDemo,
    isLoading: (units.length === 0 && isLoading) || centinela.isLoading,
    isRefreshing: centinela.isFetching,
    isError: isError || centinela.isError,
    error:
      error instanceof Error
        ? error.message
        : centinela.error instanceof Error
          ? centinela.error.message
          : null,
  };
}
